// Schemas for routes-calendar.ts (browser-safe zod). Calendly scheduling (PRD §12).
import { z } from 'zod';
import { AppointmentSchema, CapabilityReportSchema } from './api.js';
import { CalendlyScopeSchema, ConnectionStatusSchema } from './enums.js';
import { JointProjectLinkSchema } from './api-joint-appointment.js';

const Iso = z.string();
const Id = z.string().min(1).max(64);
const Email = z.email().max(254);
const Tz = z.string().min(1).max(64);

export const BOOKING_INTENT_STATUSES = ['pending', 'submitted', 'confirmed', 'failed', 'uncertain'] as const;
export const BookingIntentStatusSchema = z.enum(BOOKING_INTENT_STATUSES);
export type BookingIntentStatus = z.infer<typeof BookingIntentStatusSchema>;

export const BOOKING_INTENT_STATUS_LABELS: Record<BookingIntentStatus, string> = {
  pending: 'Preparando',
  submitted: 'Enviada a Calendly',
  confirmed: 'Confirmada por Calendly',
  failed: 'No se agendó',
  uncertain: 'Resultado incierto (conciliando)',
};

/** Which Calendly connection a project books with, and why booking may be paused (A34). */
export const EffectiveCalendarSchema = z.object({
  projectId: Id,
  scope: CalendlyScopeSchema,
  source: z.enum(['project', 'workspace']),
  connectionId: Id.nullable(),
  connectionLabel: z.string().nullable(),
  connectionStatus: ConnectionStatusSchema.nullable(),
  bookable: z.boolean(),
  pausedReason: z.string().nullable(),
  capabilities: CapabilityReportSchema.nullable(),
});
export type EffectiveCalendar = z.infer<typeof EffectiveCalendarSchema>;

/** Event type as last synced from Calendly (cached on the connection). */
export const CalendlyEventTypeSchema = z.object({
  uri: z.string(),
  name: z.string(),
  active: z.boolean(),
  durationMinutes: z.number().int().nullable(),
  kind: z.string().nullable(),
  poolingType: z.string().nullable(),
  hostName: z.string().nullable(),
  schedulingUrl: z.string().nullable(),
  locationKinds: z.array(z.string()),
  requiredQuestions: z.array(z.string()),
});
export type CalendlyEventType = z.infer<typeof CalendlyEventTypeSchema>;

export const AppointmentTypeSchema = z.object({
  id: Id,
  projectId: Id,
  connectionId: Id,
  name: z.string(),
  externalEventTypeUri: z.string(),
  durationMinutes: z.number().int().nullable(),
  location: z.string().nullable(),
  timezone: z.string().nullable(),
  active: z.boolean(),
  /** Mapped, active AND belongs to the project's effective connection. */
  bookable: z.boolean(),
  notBookableReason: z.string().nullable(),
});
export type AppointmentType = z.infer<typeof AppointmentTypeSchema>;

export const ProjectCalendarSchema = z.object({
  effective: EffectiveCalendarSchema,
  mappings: z.array(AppointmentTypeSchema),
  eventTypes: z.array(CalendlyEventTypeSchema),
  eventTypesSyncedAt: Iso.nullable(),
});
export type ProjectCalendar = z.infer<typeof ProjectCalendarSchema>;

export const UpdateProjectEventTypesSchema = z.object({
  items: z
    .array(
      z.object({
        externalEventTypeUri: z.string().url().max(300),
        enabled: z.boolean(),
        location: z.string().max(300).nullable().optional(),
        timezone: Tz.nullable().optional(),
      }),
    )
    .max(100),
});

export const SyncEventTypesResultSchema = z.object({ connectionId: Id, eventTypes: z.array(CalendlyEventTypeSchema), syncedAt: Iso });

export const CalendarConnectionSchema = z.object({
  id: Id,
  label: z.string(),
  status: ConnectionStatusSchema,
  externalAccountName: z.string().nullable(),
  isWorkspaceDefault: z.boolean(),
  capabilities: CapabilityReportSchema,
  eventTypesSyncedAt: Iso.nullable(),
  webhook: z.object({
    subscribed: z.boolean(),
    scope: z.enum(['organization', 'user']).nullable(),
    callbackUrl: z.string(),
    subscribedAt: Iso.nullable(),
    lastEventAt: Iso.nullable(),
  }),
});
export type CalendarConnection = z.infer<typeof CalendarConnectionSchema>;

export const CreateCalendlyWebhookSchema = z.object({ scope: z.enum(['organization', 'user']).default('organization') });

export const AvailabilityQuerySchema = z.object({ appointmentTypeId: Id, from: Iso, to: Iso });
export const SlotSchema = z.object({ startAt: Iso, endAt: Iso.nullable() });
export type Slot = z.infer<typeof SlotSchema>;
export const AvailabilitySchema = z.object({
  appointmentTypeId: Id,
  timezone: z.string(),
  slots: z.array(SlotSchema),
  /** Served from the short-lived cache (≤ 60 s). Booking always revalidates with Calendly. */
  cached: z.boolean(),
  fetchedAt: Iso,
});
export type Availability = z.infer<typeof AvailabilitySchema>;

export const CreateBookingSchema = z.object({
  contactId: Id,
  projectId: Id,
  appointmentTypeId: Id,
  startAt: Iso,
  /** Confirmed invitee email. Missing → 422 invitee_email_required (never a placeholder). */
  inviteeEmail: Email.optional(),
  inviteeName: z.string().min(1).max(200).optional(),
  inviteeTimezone: Tz.optional(),
  answers: z.array(z.object({ question: z.string().max(300), answer: z.string().max(1000) })).max(20).optional(),
  idempotencyKey: z.string().min(8).max(200).optional(),
  /** Joint appointment (setter §08.5): the contact's other projects this visit also covers (validated server-side). */
  jointProjectIds: z.array(Id).min(1).max(3).optional(),
});
export type CreateBooking = z.infer<typeof CreateBookingSchema>;

export const BookingIntentSchema = z.object({
  id: Id,
  idempotencyKey: z.string(),
  contactId: Id,
  contactName: z.string().nullable(),
  projectId: Id,
  projectName: z.string().nullable(),
  appointmentTypeId: Id,
  appointmentTypeName: z.string().nullable(),
  connectionId: Id,
  startAt: Iso,
  inviteeEmail: z.string().nullable(),
  status: BookingIntentStatusSchema,
  error: z.string().nullable(),
  appointmentId: Id.nullable(),
  createdAt: Iso,
  updatedAt: Iso,
});
export type BookingIntent = z.infer<typeof BookingIntentSchema>;

export const AppointmentHistoryEntrySchema = z.object({ at: Iso, action: z.string(), label: z.string(), detail: z.string().nullable() });
/** One reminder of the single built-in mechanism (C8). `shiftedFrom`: ideal time before moving it inside the contact window (C7). */
export const ReminderSchema = z.object({
  id: Id,
  channel: z.enum(['whatsapp', 'voice']),
  kind: z.string().default('hours_before'),
  dueAt: Iso,
  shiftedFrom: Iso.nullable().default(null),
  status: z.string(),
  skipReason: z.string().nullable(),
});

export const AppointmentDetailSchema = AppointmentSchema.extend({
  connectionId: Id,
  appointmentTypeId: Id.nullable(),
  appointmentTypeName: z.string().nullable(),
  externalEventUri: z.string().nullable(),
  externalInviteeUri: z.string().nullable(),
  previousAppointmentId: Id.nullable(),
  nextAppointmentId: Id.nullable(),
  reminders: z.array(ReminderSchema),
  history: z.array(AppointmentHistoryEntrySchema),
  actions: z.object({
    cancel: z.object({ allowed: z.boolean(), reason: z.string().nullable() }),
    reschedule: z.object({ mode: z.enum(['link', 'advisor_task', 'unavailable']), reason: z.string().nullable() }),
  }),
  /** Joint appointment (setter §08.5): the other opportunities this visit also covers; empty for a single project. */
  jointProjects: z.array(JointProjectLinkSchema).default([]),
});
export type AppointmentDetail = z.infer<typeof AppointmentDetailSchema>;

export const BookingResultSchema = z.object({
  intent: BookingIntentSchema,
  appointment: AppointmentDetailSchema.nullable(),
  /** True when the request was a retry of an already-confirmed booking (no second booking was made). */
  replayed: z.boolean(),
});
export type BookingResult = z.infer<typeof BookingResultSchema>;

export const CancelAppointmentSchema = z.object({ reason: z.string().min(3).max(500) });
export const RequestRescheduleSchema = z.object({ note: z.string().max(1000).optional() });
export const RescheduleResultSchema = z.object({
  appointment: AppointmentDetailSchema,
  mode: z.enum(['link', 'advisor_task']),
  rescheduleUrl: z.string().nullable(),
  handoffId: Id.nullable(),
});

export const TestBookingSchema = z.object({
  projectId: Id,
  appointmentTypeId: Id,
  inviteeEmail: Email,
  inviteeName: z.string().min(1).max(200).default('Reserva de prueba Estateflow'),
  /** Explicit confirmation: this creates a REAL Calendly booking and then cancels it. */
  confirm: z.literal(true),
});
export const TestBookingResultSchema = z.object({
  ok: z.boolean(),
  steps: z.array(z.object({ step: z.enum(['read_availability', 'book', 'cancel']), state: z.enum(['verified', 'failed', 'unsupported', 'skipped']), detail: z.string().nullable() })),
  /** When the test booking could not be cancelled automatically: cancel it manually here. */
  manualCancelUrl: z.string().nullable(),
  capabilities: CapabilityReportSchema,
});

export const BookingIntentListQuerySchema = z.object({ status: z.enum(['needs_attention', 'uncertain', 'failed', 'all']).default('needs_attention') });

export const CalendlyWebhookAckSchema = z.object({ received: z.boolean(), duplicate: z.boolean() });

